import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Container,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';

interface DashboardLayoutProps {
    children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Wallets', icon: <AccountBalanceWalletIcon />, path: '/dashboard/wallets' },
        { text: 'Transactions', icon: <ReceiptIcon />, path: '/dashboard/transactions' },
        { text: 'Categories', icon: <CategoryIcon />, path: '/dashboard/categories' },
        { text: 'Profile', icon: <PersonIcon />, path: '/dashboard/profile' },
    ];

    const drawer = (
        <List>
            {menuItems.map((item) => (
                <ListItem
                    key={item.text}
                    component="div"
                    disablePadding
                >
                    <Box
                        onClick={() => {
                            navigate(item.path);
                            if (isMobile) setMobileOpen(false);
                        }}
                        sx={{
                            width: '100%',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            bgcolor: location.pathname === item.path ? 'action.selected' : 'transparent',
                            '&:hover': {
                                bgcolor: 'action.hover',
                            }
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                    </Box>
                </ListItem>
            ))}
            <ListItem component="div" disablePadding>
                <Box
                    onClick={handleLogout}
                    sx={{
                        width: '100%',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                            bgcolor: 'action.hover',
                        }
                    }}
                >
                    <ListItemIcon><LogoutIcon /></ListItemIcon>
                    <ListItemText primary="Logout" />
                </Box>
            </ListItem>
        </List>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    bgcolor: '#b2ceff',
                }}
            >
                <Toolbar>
                    {isMobile && (
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, color: '#27559b' }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <img
                        src="/duitleh.png"
                        alt="Duitleh Logo"
                        style={{ width: 130, height: "auto", marginBottom: 0 }}
                    />
                    {!isMobile && menuItems.map((item) => (
                        <Button
                            key={item.text}
                            color="inherit"
                            onClick={() => navigate(item.path)}
                            sx={{
                                mx: 1,
                                bgcolor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
                            }}
                        >
                            {item.text}
                        </Button>
                    ))}
                    {!isMobile && (
                        <Button color="inherit" onClick={handleLogout}>
                            Logout
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { md: 240 }, flexShrink: { md: 0 } }}
            >
                {/* Mobile drawer */}
                {isMobile && (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
                        }}
                    >
                        {drawer}
                    </Drawer>
                )}
                {/* Desktop drawer */}
                {!isMobile && (
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: 240,
                                mt: '64px' // Height of AppBar
                            },
                        }}
                        open
                    >
                        {drawer}
                    </Drawer>
                )}
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { md: `calc(100% - 240px)` },
                    mt: '64px', // Height of AppBar
                }}
            >
                <Container maxWidth="lg">
                    {children}
                </Container>
            </Box>
        </Box>
    );
};
